import React from 'react';
import { CheckCircle2, XCircle } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

interface StatusBadgeProps {
  status?: 'pending' | 'in_progress' | 'completed';
  formFilled?: boolean;
  referencesCompleted?: number;
  referencesTotal?: number;
}

// Custom indicator for consistent styling across app
export const StatusIndicator = ({ formFilled, referencesCompleted = 0, referencesTotal = 0 }) => {
  // Candidate form not submitted yet
  if (!formFilled) {
    return <XCircle className="w-5 h-5 text-red-300 flex-shrink-0" />;
  }
  
  // Candidate form submitted but has incomplete references
  // Only consider as pending if both:
  // 1. There are actual references to check (totalRefs > 0)
  // 2. Not all references have completed forms (completedRefs < totalRefs)
  if (referencesCompleted < referencesTotal && referencesTotal > 0) {
    return (
      <div className="w-5 h-5 flex-shrink-0 rounded-full border-2 border-orange-400 flex items-center justify-center">
        <div className="w-2 h-0.5 bg-orange-400"></div>
      </div>
    );
  }
  
  // Everything complete - either no references required or all references completed
  return <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />;
};

const StatusBadge = ({ status, formFilled, referencesCompleted = 0, referencesTotal = 0 }: StatusBadgeProps) => {
  const { t } = useLanguage();
  
  // If we have form status data, use our new custom status logic
  if (formFilled !== undefined) {
    // Form not filled
    if (!formFilled) {
      return <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">{t('candidates.status.pendingCandidate')}</span>;
    }
    
    // Form filled but references pending - only if there are actually references to complete
    // and not all of them have been completed
    if (referencesCompleted < referencesTotal && referencesTotal > 0) {
      return <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">{t('candidates.status.pendingReferences')}</span>;
    }
    
    // All good - either no references or all completed
    return <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">{t('candidates.status.done')}</span>;
  }
  
  // Legacy mode - backwards compatibility
  const getStatusStyles = () => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${getStatusStyles()}`}>
      {status?.replace('_', ' ').charAt(0).toUpperCase() + status?.slice(1).replace('_', ' ')}
    </span>
  );
};

export default StatusBadge;